import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type PageResult } from '@/lib/api'

/**
 * The two manual gates of spec Requirement 4.
 *
 * ① Task verification — `staff_manual` tasks a member has submitted. Biconomy
 *    binding is automatic and never lands here.
 * ② Reward approval — fully complete groups set to `require_approval`.
 *    `auto_credit` groups skip this queue entirely.
 *
 * Both stages return `2001` when the row has already been actioned, which is the
 * case ConfirmAction is built around.
 */

export interface VerificationRow {
  member_task_id: number
  user_id: string
  display_name: string
  quest_name: string
  task_title: string
  submitted_at: string
  /**
   * The member's manual bindings, so an "X follow"-style task can be checked
   * without leaving the queue. Both are `''` when unbound, which is normal — a
   * staff_manual task need not relate to either binding.
   */
  twitter_handle: string
  biconomy_uid: string
}

export interface RewardApprovalRow {
  id: number
  user_id: string
  display_name: string
  quest_name: string
  reward_points: number
  completed_at: string
}

const VERIFICATIONS_KEY = 'verifications'
const APPROVALS_KEY = 'reward-approvals'

export function useVerifications({ page = 1, pageSize = 20 } = {}) {
  return useQuery({
    queryKey: [VERIFICATIONS_KEY, page, pageSize],
    queryFn: () =>
      api.get<PageResult<VerificationRow>>(
        `/admin/verifications?page=${page}&page_size=${pageSize}`,
      ),
  })
}

export function useRewardApprovals({ page = 1, pageSize = 20 } = {}) {
  return useQuery({
    queryKey: [APPROVALS_KEY, page, pageSize],
    queryFn: () =>
      api.get<PageResult<RewardApprovalRow>>(
        `/admin/reward-approvals?page=${page}&page_size=${pageSize}`,
      ),
  })
}

/**
 * Verifying the last outstanding task completes the group, which either moves it
 * into the approval queue or auto-credits it — so a stage ① decision can change
 * stage ②. Both queues are invalidated on every action for that reason.
 */
function useQueueMutation<T>(request: (input: T) => Promise<unknown>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [VERIFICATIONS_KEY] })
      void queryClient.invalidateQueries({ queryKey: [APPROVALS_KEY] })
    },
  })
}

export function useVerifyTask() {
  return useQueueMutation(({ id, note }: { id: number; note?: string }) =>
    api.post(`/admin/verifications/${id}/verify`, { note }),
  )
}

export function useRejectTask() {
  return useQueueMutation(({ id, note }: { id: number; note?: string }) =>
    api.post(`/admin/verifications/${id}/reject`, { note }),
  )
}

export function useApproveReward() {
  return useQueueMutation(({ id, note }: { id: number; note?: string }) =>
    api.post(`/admin/reward-approvals/${id}/approve`, { note }),
  )
}

export function useRejectReward() {
  return useQueueMutation(({ id, note }: { id: number; note?: string }) =>
    api.post(`/admin/reward-approvals/${id}/reject`, { note }),
  )
}
